import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuthContext } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/layout/DashboardLayout';
import DashboardPage from './pages/DashboardPage';
import SupportTicketsPage from './pages/SupportTicketsPage';
import CreationRequestsPage from './pages/CreationRequestsPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import MonitoringPage from './pages/MonitoringPage';
import ProspectionPage from './pages/ProspectionPage';
import { NotificationProvider } from './context/NotificationContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import IdleTimeoutWarning from './components/common/IdleTimeoutWarning';

// Créer une instance du client React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Composant pour protéger les routes nécessitant une authentification
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading, isAdmin, logout, authInitialized } = useAuthContext();
  
  // Attendre l'initialisation complète de l'authentification
  if (loading || !authInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-100">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="mt-4 text-base-content/70">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background flex-col">
        <div className="admin-card p-6 max-w-md text-center">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-16 h-16 text-error mx-auto mb-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <h2 className="text-2xl font-bold mb-2">Accès refusé</h2>
          <p className="mb-4 text-gray-600">Vous n'avez pas les droits d'administration nécessaires.</p>
          <button 
            onClick={logout}
            className="btn btn-outline"
          >
            Déconnexion
          </button>
        </div>
      </div>
    );
  }

  return children;
};

// Composant pour rediriger les utilisateurs déjà connectés
const PublicRoute = ({ children }) => {
  const { currentUser, loading, authInitialized } = useAuthContext();
  
  if (loading || !authInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-100">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="mt-4 text-base-content/70">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (currentUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Créez un composant pour le ToastContainer qui utilise le thème actuel
const ThemedToastContainer = () => {
  const { theme } = useTheme();
  
  return (
    <ToastContainer
      position="bottom-right"
      autoClose={3000}
      hideProgressBar={true}
      newestOnTop={true}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss={false}
      draggable
      pauseOnHover={false}
      limit={3}
      theme={theme === 'dark' ? 'dark' : 'light'}
    />
  );
};

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <NotificationProvider>
            <BrowserRouter>
              <>
                <Routes>
                  {/* Route publique pour la connexion */}
                  <Route 
                    path="/login" 
                    element={
                      <PublicRoute>
                        <LoginPage />
                      </PublicRoute>
                    } 
                  />
                  
                  {/* Routes protégées pour le tableau de bord */}
                  <Route 
                    path="/" 
                    element={
                      <ProtectedRoute>
                        <DashboardLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="dashboard" element={<DashboardPage />} />
                    <Route path="clients" element={<ClientsPage />} />
                    <Route path="clients/:uid" element={<ClientDetailPage />} />
                    {/* Anciennes routes fusionnées dans « Clients » (migration sans casse) */}
                    <Route path="users" element={<Navigate to="/clients" replace />} />
                    <Route path="websites" element={<Navigate to="/clients" replace />} />
                    <Route path="creation-requests" element={<CreationRequestsPage />} />
                    <Route path="support" element={<SupportTicketsPage />} />
                    {/* Prospection (étape 1) : import Apify, file d'appel, fiche, tunnel */}
                    <Route path="prospection" element={<ProspectionPage />} />
                    <Route path="prospection/:placeId" element={<ProspectionPage />} />
                    {/* Écran Monitoring (lot I5, lecture seule) : incidents serveur + emails en échec */}
                    <Route path="monitoring" element={<MonitoringPage />} />
                    {/* Routes supprimées : abonnements, analytiques, paramètres */}
                  </Route>
                  
                  {/* Redirection par défaut vers le tableau de bord */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
                <ThemedToastContainer />
                <IdleTimeoutWarning />
              </>
            </BrowserRouter>
          </NotificationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
